import { IS_ENCRYPTION_ENABLED } from 'src/config';
import { Builder, appendFormData, getHeaders, getQueryString } from './utils';
import { getEncryptParams } from 'src/keycloak';
import {
  ICreateIdentityProviderParams,
  ICreateIdentityProviderResponse,
  ICreateResourceProviderParams,
  IGetIdentityProviderResponse,
  IGetResourceProvider,
  IUpdateIdentityProviderParams,
  IUpdateResourceProviderParams
} from 'src/types/settings';
import { getPayload } from 'src/utils/encryption';
import { blobToBuffer, decryptFileData } from 'src/utils/files/file_encryption';
import { DataApiInputParams } from 'src/types/api';

export default (builder: Builder) => ({
  getIdentityProvider: builder.query<any, void>({
    async queryFn(args, { getState }, extraOptions, fetchWithBQ) {
      let url = `providers/identity_provider`;

      if (IS_ENCRYPTION_ENABLED) {
        url = `${url}?payload=${btoa(getPayload('', getEncryptParams()))}`;
      }

      return fetchWithBQ({
        url: url,
        ...getHeaders(url)
      });
    },
    providesTags: ['IdentityProvider']
  }),
  createIdentityProvider: builder.mutation<any, ICreateIdentityProviderParams>({
    async queryFn(
      args: ICreateIdentityProviderParams,
      { getState },
      extraOptions,
      fetchWithBQ
    ) {
      let url = `providers/identity_provider`;
      const formData = appendFormData(args.params);
      return fetchWithBQ({
        url: url,
        method: 'POST',
        ...getHeaders(url, formData),
        body: formData
      });
    },
    invalidatesTags: ['IdentityProvider']
  }),
  updateIdentityProvider: builder.mutation<any, IUpdateIdentityProviderParams>({
    async queryFn(args, { getState }, extraOptions, fetchWithBQ) {
      let url = `providers/identity_provider`;
      let formData = appendFormData(args.params);

      return fetchWithBQ({
        url: url,
        method: 'PATCH',
        body: formData,
        ...getHeaders(url, formData)
      });
    },
    invalidatesTags: ['IdentityProvider']
  }),
  deleteIdentityProvider: builder.mutation({
    async queryFn(
      args: { params: { id: number } },
      { getState },
      extraOptions,
      fetchWithBQ
    ) {
      let url = `providers/identity_provider`;
      const formData = appendFormData(args.params);
      return fetchWithBQ({
        url: url,
        method: 'Delete',
        ...getHeaders(url, formData),
        body: formData
      });
    },
    invalidatesTags: ['IdentityProvider', 'ResourceProvider']
  }),

  getResourceProvider: builder.query<
    unknown,
    { params: IGetResourceProvider } | void
  >({
    async queryFn(args, { getState }, extraOptions, fetchWithBQ) {
      const queryString = getQueryString(args && args.params);
      let url = `providers/resource_provider${
        queryString.length ? '?' + queryString : ''
      }`;

      return fetchWithBQ({
        url: url,
        ...getHeaders(url)
      });
    },
    providesTags: ['ResourceProvider']
  }),

  createResourceProvider: builder.mutation<
    unknown,
    ICreateResourceProviderParams
  >({
    async queryFn(args, _, __, fetchWithBQ) {
      let url = `providers/resource_provider`;
      let formData = appendFormData(args.params);

      return fetchWithBQ({
        url: url,
        method: 'POST',
        body: formData,
        ...getHeaders(url, formData)
      });
    },
    invalidatesTags: ['ResourceProvider']
  }),
  updateResourceProvider: builder.mutation<
    unknown,
    IUpdateResourceProviderParams
  >({
    async queryFn(args, _, __, fetchWithBQ) {
      let url = `providers/resource_provider`;
      let formData = appendFormData(args.params);

      return fetchWithBQ({
        url: url,
        method: 'PATCH',
        body: formData,
        ...getHeaders(url, formData)
      });
    },
    invalidatesTags: ['ResourceProvider']
  }),
  deleteResourceProvider: builder.mutation<unknown, { params: { id: number } }>(
    {
      async queryFn(args, _, __, fetchWithBQ) {
        let url = `providers/resource_provider`;
        let formData = appendFormData(args.params);

        return fetchWithBQ({
          url: url,
          method: 'DELETE',
          body: formData,
          ...getHeaders(url, formData),
          responseHandler: IS_ENCRYPTION_ENABLED ? 'text' : 'json'
        });
      },
      invalidatesTags: ['ResourceProvider']
    }
  ),
  downloadCertificate: builder.query({
    async queryFn(
      args: DataApiInputParams,
      { getState },
      extraOptions,
      fetchWithBQ
    ) {
      let url = `app/certificates`;
      if (IS_ENCRYPTION_ENABLED) {
        url = `${url}?payload=${btoa(getPayload('', getEncryptParams()))}`;
      }
      const result: any = await fetchWithBQ({
        url: url,
        ...getHeaders(url),
        returnUnparsedResponse: true,
        responseHandler: async (response) => ({
          ret_val: await response.blob()
        })
      });
      const bufferData = (await blobToBuffer(
        result.data.ret_val as Blob
      )) as Buffer;
      return { data: bufferData };
    }
  }),
  getAppSettings: builder.query({
    async queryFn(
      arg: DataApiInputParams,
      { getState },
      extraOptions,
      fetchWithBQ
    ) {
      let decryptKey = getEncryptParams()?.k;
      let url = `fw/resource-settings?${getQueryString(arg.params)}`;
      return fetchWithBQ({
        url: url,
        ...getHeaders(url)
      });
    },
    providesTags: ['ResourceSettings']
  }),
  updateAppSettings: builder.mutation({
    async queryFn(
      arg: DataApiInputParams,
      { getState },
      extraOptions,
      fetchWithBQ
    ) {
      let formData = appendFormData(arg.params);
      let url = `fw/resource-settings`;
      return fetchWithBQ({
        url: url,
        method: 'POST',
        body: formData,
        ...getHeaders(url, formData)
      });
    },
    invalidatesTags: ['ResourceSettings', 'ModelTiersRateLimits']
  }),
  getConfiguredAndDefaultModels: builder.query({
    async queryFn(
      arg: DataApiInputParams,
      { getState },
      extraOptions,
      fetchWithBQ
    ) {
      let url = `fw/configured-models?${getQueryString(arg.params)}`;
      return fetchWithBQ({
        url: url,
        ...getHeaders(url)
      });
    }
  }),
  getModels: builder.query({
    async queryFn(
      arg: DataApiInputParams,
      { getState },
      extraOptions,
      fetchWithBQ
    ) {
      const paramsWrapper = arg?.params as Record<string, unknown> | undefined;
      const nestedQuery = paramsWrapper?.params;
      const useNestedQuery =
        typeof nestedQuery === 'object' && !Array.isArray(nestedQuery);

      const queryStringParams = useNestedQuery
        ? (nestedQuery as Record<string, unknown>)
        : { ...paramsWrapper };

      let url = `expert-advisor/ai-models?${getQueryString(queryStringParams)}`;
      const result: any = await fetchWithBQ({
        url: url,
        method: 'GET',
        ...getHeaders(url)
      });

      if (result.error) return result;

      return { data: { data: result } };
    },
    providesTags: ['ResourceSettings']
  }),
  updateModelProperties: builder.mutation({
    async queryFn(
      arg: DataApiInputParams,
      { getState },
      extraOptions,
      fetchWithBQ
    ) {
      let formData = appendFormData(arg.params);
      let url = `fw/configured-models`;
      return fetchWithBQ({
        url: url,
        method: 'POST',
        body: formData,
        ...getHeaders(url, formData)
      });
    },
    invalidatesTags: ['ResourceSettings']
  }),
  getModelsRateLimitTiers: builder.query({
    async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
      let url = `fw/rate_limits?${getQueryString(arg.params)}`;
      return fetchWithBQ({
        url: url,
        ...getHeaders(url)
      });
    },
    providesTags: ['ModelTiersRateLimits']
  })
});
